import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import { AutoComplete } from "primereact/autocomplete";
import { useMemo, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";

import { formValidationConstants } from "../helpers/formValidations";
import { IsDefaultCheckbox } from "./IsDefaultCheckbox";

const Title = styled.h3`
  margin-bottom: 16px;
`;

const StyledAutoComplete = styled(AutoComplete)`
  .p-autocomplete-multiple-container {
    width: 100%;
  }
`;

export function ProviderAndPayerCopySection({
  initialProviders,
  initialPayers,
}: {
  initialProviders: { name: string; value: string }[];
  initialPayers: { name: string; value: string | number }[];
}) {
  const {
    control,
    trigger,
    formState: { errors },
  } = useFormContext<any>();
  const isDefault = useWatch({ control, name: "isDefault" });
  const { getFormErrorMessage, handleOnBlurField } = useFormValidation();
  const { data, isLoading } = useGetAllProvidersQuery();
  const { data: allPayers, isLoading: allPayersLoading } =
    useGetAllPayersQuery();
  const [selectedProviders, setSelectedProviders] =
    useState<{ name: string; value: string }[]>(initialProviders);
  const [selectedPayers, setSelectedPayers] =
    useState<{ name: string; value: string | number }[]>(initialPayers);
  const [filteredProviders, setFilteredProviders] = useState<
    { name: string; value: string }[]
  >([]);
  const [filteredPayers, setFilteredPayers] = useState<
    { name: string; value: number }[]
  >([]);

  const providers = useMemo(() => {
    if (isLoading || !data) return [];

    return data?.data.map((provider) => {
      return { name: provider.name, value: provider.id };
    });
  }, [data]);

  const payers = useMemo(() => {
    if (allPayersLoading || !allPayers) return [];
    return allPayers?.data.map((payer: any) => {
      return { name: payer.name, value: payer.id };
    });
  }, [allPayers]);

  const searchProvider = (event: { query: string }) => {
    setTimeout(() => {
      let filteredProvidersInternal;

      if (!event.query.trim().length) {
        filteredProvidersInternal = [...providers];
      } else {
        filteredProvidersInternal = providers.filter((provider) => {
          return provider.name
            .toLowerCase()
            .startsWith(event.query.toLowerCase());
        });
      }

      setFilteredProviders(filteredProvidersInternal);
    }, 250);
  };

  const searchPayer = (event: { query: string }) => {
    setTimeout(() => {
      let filteredPayersInternal;

      if (!event.query.trim().length) {
        filteredPayersInternal = [...payers];
      } else {
        filteredPayersInternal = payers.filter((payer: any) => {
          return payer.name.toLowerCase().startsWith(event.query.toLowerCase());
        });
      }

      setFilteredPayers(filteredPayersInternal);
    }, 250);
  };

  const onPayerSelect = (e: any, onChange: any) => {
    const selected = [...selectedPayers];
    selected.push(e.value);
    setSelectedPayers(selected);
    onChange(selected);
  };

  const onPayerUnselect = (e: any, onChange: any) => {
    const selected = selectedPayers.filter(
      (payer) => payer.name !== e.value.name
    );
    setSelectedPayers(selected);
    onChange(selected);
  };

  const onProviderSelect = (e: any, onChange: any) => {
    const selected = [...selectedProviders];
    selected.push(e.value);
    setSelectedProviders(selected);
    onChange(selected);
  };

  const onProviderUnselect = (e: any, onChange: any) => {
    const selected = selectedProviders.filter(
      (provider) => provider.name !== e.value.name
    );
    setSelectedProviders(selected);
    onChange(selected);
  };
  return (
    <div className="block bg-white border-round p-4 mb-4">
      <Title>Provider and Payer Section</Title>
      <IsDefaultCheckbox />
      {isDefault ? null : (
        <div className="formgrid grid">
          <div className="field col-6">
            <Label htmlFor="hospitalName">Provider</Label>
            <Controller
              data-testid="provider"
              name="provider"
              control={control}
              rules={{
                required: isDefault
                  ? false
                  : formValidationConstants.PROVIDER.PROVIDER,
              }}
              render={({ field: { onChange, value, onBlur } }) => (
                <>
                  <StyledAutoComplete
                    suggestions={filteredProviders}
                    dropdown
                    multiple
                    field="name"
                    id="provider"
                    className="w-full"
                    value={selectedProviders}
                    completeMethod={searchProvider}
                    onSelect={(e) => onProviderSelect(e, onChange)}
                    onUnselect={(e) => onProviderUnselect(e, onChange)}
                    dropdownAriaLabel="Select Provider"
                    placeholder="Select Provider"
                    onChange={onChange}
                    onBlur={() => {
                      handleOnBlurField({ onBlur, field: "provider", trigger });
                    }}
                  />
                  {getFormErrorMessage("provider", errors)}
                </>
              )}
            />
          </div>
          <div className="field col-6">
            <Label htmlFor="payerName">Payer Name</Label>
            <Controller
              name="payerName"
              control={control}
              rules={{
                required: isDefault
                  ? false
                  : formValidationConstants.PAYER_NAME.NAME,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <StyledAutoComplete
                    dropdown
                    field="name"
                    id="payerName"
                    value={selectedPayers}
                    multiple
                    className="w-full"
                    suggestions={filteredPayers}
                    completeMethod={searchPayer}
                    onSelect={(e) => onPayerSelect(e, onChange)}
                    onUnselect={(e) => onPayerUnselect(e, onChange)}
                    dropdownAriaLabel="Select Payer"
                    placeholder="Select Payer"
                    onChange={onChange}
                    onBlur={() => {
                      handleOnBlurField({
                        onBlur,
                        field: "payerName",
                        trigger,
                      });
                    }}
                  />
                  {getFormErrorMessage("payerName", errors)}
                </>
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}
